<script lang="ts">
  import Button from "../shared/Button.svelte";
  import { onMount } from "svelte";
  import {
    SYNC_DASHBOARD_REFRESH_EVENT,
    triggerOpportunityDataRefresh,
  } from "../../lib/syncDashboardRefresh";
  import SyncHeader from "./syncHeader.svelte";
  import KPIBar from "./kpiBar.svelte";
  import NavigationTabs from "./navigationTabs.svelte";
  import SystemsOverview from "./systemsOverview.svelte";
  import OpportunitiesTable from "./opportunitiesTable.svelte";
  import OpportunityDrawer from "./opportunityDrawer.svelte";
  import ActivityFeed from "./activityFeed.svelte";
  import ConflictsSummary from "./conflictsSummary.svelte";
  import EngagementInvitationsPanel from "./engagementInvitationsPanel.svelte";
  import type {
    SyncStatus,
    OpportunitiesResponse,
    OpportunityDetailed,
    Activity,
  } from "./types";

  // @ts-ignore
  const baseUrl = `${process.env.SVELTE_APP_REMOTE_URL}/api`;

  let syncStatus: SyncStatus | null = null;
  let opportunitiesData: OpportunitiesResponse | null = null;
  let activityFeed: Activity[] = [];
  let loading = false;
  let error = "";
  let isVisible = false;
  let lastSyncTime = "";
  let retrying = false;
  let syncProgress: { current: number; total: number; message: string } | null =
    null;
  let activeTab = "overview";
  let selectedOpportunity: OpportunityDetailed | null = null;
  let drawerOpen = false;
  let filterStatus = "all";

  // Load visibility preference from localStorage
  onMount(() => {
    const savedVisibility = localStorage.getItem("syncStatusDashboardVisible");
    if (savedVisibility !== null) {
      isVisible = savedVisibility === "true";
    } else {
      isVisible = true;
      localStorage.setItem("syncStatusDashboardVisible", "true");
    }
    if (isVisible) fetchAllData();
    updateLastSyncTime();

    // Setup auto-refresh for activity feed (every 30 seconds)
    const interval = setInterval(() => {
      if (isVisible) fetchActivityFeed();
    }, 30000);

    window.addEventListener(SYNC_DASHBOARD_REFRESH_EVENT, refreshDashboardData);

    return () => {
      clearInterval(interval);
      window.removeEventListener(SYNC_DASHBOARD_REFRESH_EVENT, refreshDashboardData);
    };
  });

  const updateLastSyncTime = () => {
    const now = new Date();
    lastSyncTime = now.toISOString();
  };

  const fetchAllData = async () => {
    await Promise.all([
      fetchSyncStatus(),
      fetchOpportunitiesDetailed(),
      fetchActivityFeed(),
    ]);
  };

  const fetchSyncStatus = async () => {
    const isInitial = syncStatus === null;
    if (isInitial) loading = true;
    error = "";
    try {
      const res = await fetch(`${baseUrl}/sync/status-overview`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      syncStatus = await res.json();
      updateLastSyncTime();
    } catch (err: any) {
      error = err?.message || "Failed to fetch sync status";
      console.error("Failed to fetch sync status:", err);
    } finally {
      if (isInitial) loading = false;
    }
  };

  const fetchOpportunitiesDetailed = async () => {
    try {
      const res = await fetch(`${baseUrl}/sync/opportunities-detailed`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      opportunitiesData = await res.json();
      if (selectedOpportunity) {
        const updated = opportunitiesData?.opportunities?.find(
          (opp) => opp.id === selectedOpportunity?.id
        );
        if (updated) selectedOpportunity = updated;
      }
    } catch (err: any) {
      console.error("Failed to fetch opportunities:", err);
    }
  };

  const fetchActivityFeed = async () => {
    try {
      const res = await fetch(`${baseUrl}/sync/activity-feed`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      activityFeed = data.activities;
    } catch (err: any) {
      console.error("Failed to fetch activity feed:", err);
    }
  };

  const toggleVisibility = () => {
    isVisible = !isVisible;
    localStorage.setItem("syncStatusDashboardVisible", String(isVisible));
    if (isVisible) fetchAllData();
  };

  const runSyncWithProgress = async (
    steps: Array<{
      progress: number;
      message: string;
      action?: () => Promise<any>;
    }>,
  ) => {
    loading = true;
    syncProgress = { current: 0, total: 100, message: "Starting sync..." };
    try {
      for (const step of steps) {
        syncProgress = {
          current: step.progress,
          total: 100,
          message: step.message,
        };
        if (step.action) await step.action();
      }
      syncProgress = { current: 100, total: 100, message: "Sync completed!" };
      triggerOpportunityDataRefresh();
      setTimeout(() => {
        syncProgress = null;
      }, 1000);
    } catch (err: any) {
      console.error("Sync failed:", err);
      syncProgress = { current: 0, total: 100, message: "Sync failed!" };
      setTimeout(() => {
        syncProgress = null;
      }, 3000);
    } finally {
      loading = false;
    }
  };

  const handleSyncEverything = async () => {
    await runSyncWithProgress([
      {
        progress: 10,
        message: "Fetching opportunities from AWS...",
        action: () => fetch(`${baseUrl}/sync/from-aws`).then((r) => r.json()),
      },
      {
        progress: 50,
        message: "Pushing local changes to AWS...",
        action: () =>
          fetch(`${baseUrl}/sync/to-aws`, { method: "POST" }).then((r) =>
            r.json(),
          ),
      },
      {
        progress: 80,
        message: "Processing synced opportunities...",
        action: () => new Promise((resolve) => setTimeout(resolve, 1500)),
      },
    ]);
  };

  const handleSyncFromAws = async () => {
    await runSyncWithProgress([
      { progress: 20, message: "Connecting to AWS..." },
      {
        progress: 50,
        message: "Fetching opportunities from AWS...",
        action: () => fetch(`${baseUrl}/sync/from-aws`).then((r) => r.json()),
      },
      {
        progress: 85,
        message: "Processing received data...",
        action: () => new Promise((resolve) => setTimeout(resolve, 1000)),
      },
    ]);
  };

  const handleSyncToAws = async () => {
    await runSyncWithProgress([
      { progress: 20, message: "Preparing local data..." },
      {
        progress: 55,
        message: "Pushing changes to AWS...",
        action: () =>
          fetch(`${baseUrl}/sync/to-aws`, { method: "GET" }).then((r) =>
            r.json(),
          ),
      },
      {
        progress: 85,
        message: "Confirming sync with AWS...",
        action: () => new Promise((resolve) => setTimeout(resolve, 1000)),
      },
    ]);
  };
  $: syncActions = [
    {
      label: "Sync Everything",
      description: "Full bidirectional sync",
      badgeClass: "bg-indigo-500/20 text-indigo-400",
      iconPaths: [
        "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
      ],
      handler: handleSyncEverything,
    },
    {
      label: "Sync from AWS",
      description: "Pull latest data from AWS",
      badgeClass: "bg-sky-500/20 text-sky-400",
      iconPaths: ["M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"],
      handler: handleSyncFromAws,
    },
    {
      label: "Sync to AWS",
      description: "Push local changes to AWS",
      badgeClass: "bg-emerald-500/20 text-emerald-400",
      iconPaths: ["M17 4v12M7 20v-12", "M21 16l-4 4-4-4M3 8l4-4 4 4"],
      handler: handleSyncToAws,
    },
  ];

  const handleRetrySync = async (opp: OpportunityDetailed) => {
    if (!opp.id) {
      console.error("Cannot retry sync: opportunity ID is missing");
      return;
    }

    retrying = true;
    try {
      const res = await fetch(`${baseUrl}/sync/opportunity/${opp.id}`, {
        method: "POST",
      });

      if (!res.ok) {
        throw new Error(`Failed to sync opportunity: ${res.statusText}`);
      }

      const result = await res.json();
      console.log("Retry sync result:", result);

      // Refresh data
      triggerOpportunityDataRefresh();

      // Close drawer and show success
      closeDrawer();
    } catch (err: any) {
      console.error("Retry sync failed:", err);
      alert(`Failed to sync opportunity: ${err.message}`);
    } finally {
      retrying = false;
    }
  };

  const selectOpportunity = async (opp: OpportunityDetailed) => {
    selectedOpportunity = opp;
    drawerOpen = true;
    await fetchOpportunitiesDetailed();
  };

  const closeDrawer = () => {
    drawerOpen = false;
    setTimeout(() => {
      selectedOpportunity = null;
    }, 300);
  };

  const handleKPIClick = (status: string) => {
    filterStatus = status;
    activeTab = "opportunities";
  };

  const handleTabChange = (tab: string) => (activeTab = tab);

  const refreshDashboardData = () => {
    if (isVisible) fetchAllData();
  };

  // Computed values
  $: totalOpportunities = syncStatus?.totalInAws || 0;
  $: fullySynced = syncStatus?.syncedToSharedDb || 0;
  $: syncInProgress = syncStatus?.notSynced || 0;
  $: awsTotal = syncStatus?.totalInAws || 0;
  $: awsSynced = syncStatus?.syncedToSharedDb || 0;
  $: acelinkTotal = fullySynced;
  $: conflicts = opportunitiesData?.conflicts || 0;
  $: errors = opportunitiesData?.errors || 0;

  // Global health status
  $: globalHealth =
    errors > 0 ? "Error" : conflicts > 0 ? "Degraded" : "Healthy";

  // Filtered opportunities
  $: filteredOpportunities = (opportunitiesData?.opportunities || []).filter(
    (opp) => {
      if (filterStatus === "all") return true;
      if (filterStatus === "synced") return opp.status === "synced";
      if (filterStatus === "pending") return opp.status === "not_synced";
      if (filterStatus === "error") return opp.status === "error";
      if (filterStatus === "conflict") return opp.status === "conflict";
      return true;
    },
  );
</script>

<div class="mb-8 font-sans">
  <SyncHeader
    {lastSyncTime}
    {globalHealth}
    {loading}
    {isVisible}
    {syncProgress}
    {syncActions}
    controlsDisabled={!isVisible}
    onToggleVisibility={toggleVisibility}
  />

  {#if isVisible}
    <div class="px-0 py-3 sm:py-5">
      {#if loading}
        <div
          class="text-center py-16 px-8 bg-[#24283a] rounded-xl border border-white/10 text-white"
        >
          <div
            class="border-4 border-white/10 border-t-white rounded-full w-12 h-12 animate-spin mx-auto mb-4"
          />
          <p>Loading sync status...</p>
        </div>
      {:else if error}
        <div
          class="text-center py-16 px-8 bg-[#24283a] rounded-xl border border-white/10 text-white"
        >
          <p class="text-xl font-bold text-red-500 mb-2">
            Error Loading Dashboard
          </p>
          <p class="text-white/70 mb-4">{error}</p>
          <Button size="lg" on:click={fetchAllData}>Retry</Button>
        </div>
      {:else}
        <div
          class="min-w-0 overflow-hidden rounded-xl border border-white/10 bg-[#24283a]/70 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] sm:p-5 lg:p-6"
        >
          <KPIBar
            {totalOpportunities}
            {fullySynced}
            {syncInProgress}
            {conflicts}
            {errors}
            onKpiClick={handleKPIClick}
          />

          <NavigationTabs {activeTab} onTabChange={handleTabChange} />

          <div class="tab-content min-w-0">
            {#if activeTab === "overview"}
              <div class="overview-content flex min-w-0 flex-col gap-4">
                <SystemsOverview
                  totalOpportunities={awsTotal}
                  fullySynced={awsSynced}
                  {acelinkTotal}
                  {errors}
                  {lastSyncTime}
                />
                <ActivityFeed activities={activityFeed.slice(0, 10)} />
              </div>
            {/if}

            {#if activeTab === "invitations"}
              <EngagementInvitationsPanel />
            {/if}

            {#if activeTab === "opportunities"}
              <OpportunitiesTable
                opportunities={filteredOpportunities}
                bind:filterStatus
                onSelectOpportunity={selectOpportunity}
              />
            {/if}

            {#if activeTab === "conflicts"}
              <ConflictsSummary {errors} />
            {/if}

            {#if activeTab === "activity"}
              <ActivityFeed activities={activityFeed} showTitle={true} />
            {/if}
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if drawerOpen && selectedOpportunity}
    <OpportunityDrawer
      opportunity={selectedOpportunity}
      {retrying}
      onClose={closeDrawer}
      onRetrySync={handleRetrySync}
    />
  {/if}
</div>
